import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SparklesIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { znajdzZamiennikFalownika } from '../../api/elektryka'
import { KRAJ } from '../../kraj'
import { formatPrice } from '../../utils'

/**
 * Propozycja innego falownika, gdy para nie spina się elektrycznie.
 *
 * PO CO: komunikat „prąd modułu przekracza wejście falownika" jest dla
 * użytkownika asystenta ślepą uliczką — on nie dobiera sprzętu po prądzie,
 * dostawał więc problem bez wyjścia. Tu dostaje konkretny sprzęt, przy którym
 * ten sam zestaw modułów zadziała.
 *
 * Szukamy DOPIERO po kliknięciu, nie przy rozwinięciu doboru: backend przelicza
 * pełny dobór dla każdego kandydata, więc to zapytanie ma sens tylko wtedy, gdy
 * ktoś faktycznie chce zamiennik.
 *
 * STRONA DYMKA bierze się z pozycji karty w siatce wariantów — ten sam chwyt
 * co na ekranie potwierdzenia zestawu (`STRONA_DYMKA` w PotwierdzenieZestawu):
 * pierwszy wariant („Najtańszy zestaw") wypuszcza dymek w lewo, drugi
 * („Najlepszy balans") w prawo, więc dymki nie wchodzą na siebie ani nie
 * zasłaniają sąsiedniej karty. Na wąskich ekranach nie ma czego rozsuwać —
 * dymek wraca pod przycisk (`xl:` włącza pozycjonowanie bezwzględne).
 */
const ZamiennikFalownika = ({ panelModelId, inverterModelId, mocDocelowaKwp, orientacje = 1, doLewej = true }) => {
  const { t } = useTranslation('elektryka')
  const [stan, setStan] = useState('start')   // start | szuka | jest | brak | blad
  const [propozycja, setPropozycja] = useState(null)
  const [komunikat, setKomunikat] = useState('')

  const szukaj = async () => {
    setStan('szuka')
    const res = await znajdzZamiennikFalownika({
      panel_model_id: panelModelId,
      inverter_model_id: inverterModelId,
      moc_docelowa_kwp: mocDocelowaKwp || null,
      orientacje,
    })
    if (!res.ok) { setKomunikat(res.error); setStan('blad'); return }
    if (res.data.propozycja) { setPropozycja(res.data.propozycja); setStan('jest'); return }
    setKomunikat(res.data.powod || t('zamiennik.brak'))
    setStan('brak')
  }

  // Zwykła spacja przed walutą, jak dotąd (formatKwota wstawia twardą).
  const cena = propozycja?.cena_od != null
    ? `${formatPrice(Math.round(propozycja.cena_od))} ${KRAJ.symbol}`
    : null

  return (
    <div className="relative">
      <button
        type="button"
        onClick={szukaj}
        disabled={stan === 'szuka'}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-blue-200
                   bg-white text-blue-700 text-sm font-semibold hover:bg-blue-50
                   disabled:opacity-60 transition-colors"
      >
        {stan === 'szuka'
          ? <ArrowPathIcon className="w-4 h-4 animate-spin" />
          : <SparklesIcon className="w-4 h-4" />}
        {stan === 'szuka' ? t('zamiennik.szukam') : t('zamiennik.zaproponuj')}
      </button>

      {stan !== 'start' && stan !== 'szuka' && (
        <div className={`relative mt-3 rounded-xl border border-blue-200 bg-blue-50/70 p-3 shadow-sm
                         xl:absolute xl:top-0 xl:mt-0 xl:w-[260px] xl:z-10 ${
                           doLewej ? 'xl:right-full xl:mr-4' : 'xl:left-full xl:ml-4'
                         }`}>
          {/* Grot celuje w przycisk, więc stoi po stronie PRZECIWNEJ do dymka —
              tak samo jak na ekranie potwierdzenia zestawu. */}
          <span className={`hidden xl:block absolute top-3 w-3.5 h-3.5 rotate-45 bg-blue-50 ${
            doLewej
              ? '-right-[8px] border-r border-t border-blue-300'
              : '-left-[8px] border-l border-b border-blue-300'
          }`} />

          <p className="text-xs font-bold text-blue-800 flex items-center gap-1.5 mb-2">
            <SparklesIcon className="w-4 h-4" />
            {t('zamiennik.proponuje')}
          </p>

          {stan === 'jest' && propozycja && (
            <>
              <Link to={propozycja.model_url}
                    className="font-semibold text-sm text-gray-900 hover:text-blue-700 leading-snug block">
                {propozycja.nazwa}
              </Link>
              <p className="text-xs text-gray-600 mt-1">
                {propozycja.moc_kw != null ? `${propozycja.moc_kw} kW` : null}
                {propozycja.mppt_count ? ` · ${propozycja.mppt_count} MPPT` : ''}
                {cena ? t('zamiennik.od', { cena }) : ''}
              </p>
              {/* Mówimy WPROST, co ta podmiana załatwia — bez tego to tylko
                  „inny falownik", a użytkownik nie wie, czemu miałby go chcieć. */}
              <p className="text-[11px] text-blue-800/80 leading-5 mt-2 pt-2 border-t border-blue-200/70">
                {t('zamiennik.wyjasnienie')}
              </p>
            </>
          )}

          {(stan === 'brak' || stan === 'blad') && (
            <p className="text-xs text-gray-600 leading-5">{komunikat}</p>
          )}
        </div>
      )}
    </div>
  )
}

export default ZamiennikFalownika
